import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { type PlainGroupMembership } from "@/queries/group-queries";
import type { PlainOrganizationMember } from "@/queries/organization-queries";
import { type FC } from "react";

type MemberAvatarStackProps = {
    memberships?: PlainGroupMembership[];
    orgMembers?: PlainOrganizationMember[];
    maxVisible?: number;
    size?: "sm" | "md";
    className?: string;
};

const sizeClasses = {
    sm: "size-6",
    md: "size-8",
};

export const MemberAvatarStack: FC<MemberAvatarStackProps> = ({
    memberships = [],
    orgMembers = [],
    maxVisible = 3,
    size = "sm",
    className,
}) => {
    const visibleMembers = memberships
        .slice(0, maxVisible)
        .map((m) => orgMembers.find((om) => om.userId === m.subject?.id))
        .filter((m) => !!m);
    const remainingCount = memberships.length - visibleMembers.length;
    const showRemainingCount = remainingCount > 0;

    if (memberships.length === 0) {
        return <div className={cn("text-sm text-content-secondary", className)}>No members</div>;
    }

    return (
        <div className={cn("flex items-center", className)}>
            {/* Member avatars with overlap */}

            <ul
                className="group/members flex max-w-[140px] items-center whitespace-nowrap rounded-lg p-0.5"
                tabIndex={0}
                aria-label="Group members"
            >
                {visibleMembers.map((member, index) => (
                    <li
                        key={member.userId}
                        style={{ zIndex: maxVisible - index }}
                        className={cn(
                            "relative flex-shrink-0 transition-all ease-out",
                            index > 0 &&
                                "xl:max-[1380px]:group-focus-within/members:-ml-3 xl:max-[1380px]:group-hover/members:-ml-3 relative -ml-3 group-focus-within/members:-ml-2 group-hover/members:-ml-1 @[300px]:-ml-2.5",
                        )}
                        title={member.fullName}
                    >
                        <Tooltip content={member.fullName} usePortal>
                            <Avatar
                                size={24}
                                className="size-6 flex-shrink-0 rounded-full border-2 border-surface-glass bg-surface-glass ring-1 ring-border-light transition-shadow group-focus-within/members:shadow-sm group-hover/members:shadow-sm"
                            >
                                {member.avatarUrl && (
                                    <AvatarImage src={member.avatarUrl} alt={`${member.fullName}'s avatar`} />
                                )}
                                <AvatarFallback>
                                    <AvatarInitials name={member.fullName} size={24} />
                                </AvatarFallback>
                            </Avatar>
                        </Tooltip>
                    </li>
                ))}

                {/* Show +N for additional members */}
                {showRemainingCount && (
                    <li
                        className={cn(
                            "relative -ml-2 flex size-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-surface-glass bg-surface-secondary text-[11px] font-medium text-content-secondary ring-1 ring-border-light @[300px]:-ml-0.5",
                            sizeClasses[size],
                        )}
                        title={`${remainingCount} more member${remainingCount === 1 ? "" : "s"}`}
                    >
                        +{remainingCount}
                    </li>
                )}
            </ul>
        </div>
    );
};
